import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import {
  FormGroup, Button, Intent, Spinner, Switch
} from "@blueprintjs/core";
import DateTimeInput from 'components/DateTimeInput';
import FormDrawer from 'components/FormDrawer';
import { FormSection, Choice, FieldError, MoreOptions } from 'components/FormBits';
import Toast from 'helpers/toaster';
import { observable } from 'mobx';
import _ from 'lodash';
import moment from 'moment';

const DURATIONS = [
  { value: 0.25, label: '15m' },
  { value: 0.5, label: '30m' },
  { value: 1, label: '1h' },
  { value: 2, label: '2h' },
];

// Up to this many technicians on the job show as chips; more as a list.
const MAX_TECH_CHIPS = 6;

// Log time a technician spent on this job: who, when they started and for
// how long, then (folded) which work order it belongs to.
@inject('jobStore') @observer
class AddTimeLogModal extends Component {

  @observable errors = {};
  @observable showErrors = false;

  componentDidMount() {
    if (this.props.isOpen) this.onOpen();
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) this.onOpen();
    if (this.props.isOpen) this.autoPickWorkOrder();
  }

  timelogObject() {
    return this.props.jobStore.timelog.object;
  }

  assignments() {
    const { job } = this.props.jobStore;
    const attributes = job && job.object && job.object.attributes;
    return (attributes && attributes.assignments) || [];
  }

  // The selected technician's work orders on this job. Derived on every
  // render: the job's work orders load while the panel opens.
  workOrders() {
    const object = this.timelogObject();
    if (!object.user_id) return [];
    return _.filter(this.props.jobStore.jobWorkReports, function(obj) { return obj.attributes.user_id === object.user_id});
  }

  onOpen() {
    this.errors = {};
    this.showErrors = false;
    // Only one technician on the job: pick them (unless someone already is).
    const assignments = this.assignments();
    const object = this.timelogObject();
    if (!object.user_id && assignments.length === 1) {
      this.setTechnician(assignments[0].user_id, assignments[0].name);
    }
  }

  // The technician has exactly one work order on this job: pick it.
  autoPickWorkOrder() {
    const object = this.timelogObject();
    if (object.work_report_id) return;
    const workOrders = this.workOrders();
    if (workOrders.length === 1) {
      object.work_report_id = workOrders[0].id;
    }
  }

  validate() {
    const object = this.timelogObject();
    const errors = {};
    if (!object.user_id) errors.technician = 'Choose who did the work.';
    if (!object.start_time) errors.start = 'Choose when the work started.';
    const hours = parseFloat(object.hours);
    if (!(hours > 0)) errors.hours = 'Enter how long they worked, more than 0 hours.';
    this.errors = errors;
    return errors;
  }

  revalidate() {
    if (this.showErrors) this.validate();
  }

  createTimeLog() {
    let {
      jobStore,
      jobStore: {
        timelog: {
          object
        }
      }
    } = this.props;
    this.showErrors = true;
    const errors = this.validate();
    const first = ['technician', 'start', 'hours'].find((key) => errors[key]);
    if (first) {
      const el = document.getElementById(`timelog-field-${first}`);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const focusable = el.querySelector('input, select') || el.querySelector('button');
        if (focusable) focusable.focus();
      }
      return;
    }
    jobStore.createTimeLog(object)
      .then(this.onSuccessfullySaveTimeLog.bind(this))
      .catch(this.onFailCreateTimeLog.bind(this))
  }

  onSuccessfullySaveTimeLog(response) {
    this.props.onSuccess();
  }

  onFailCreateTimeLog(error) {
    if(error.response && error.response.data && error.response.data.errors) {
      Toast.show({ icon: 'warning-sign', message: error.response.data.errors, intent: Intent.DANGER })
    } else {
      Toast.show({ icon: 'warning-sign', message: "Something went wrong, failed to create TimeLog.", intent: Intent.DANGER })
    }
  }

  handleCloseTimeLogDailog() {
    this.props.onClose();
  }

  // Same as picking from the old technician list: user_id, the technician's
  // name as the description, and their assignment on this job.
  setTechnician(userId, name) {
    let {
      jobStore: {
        timelog: {
          object
        },
        job: {
          object: {
            attributes
          }
        }
      }
    } = this.props;
    const previous = object.user_id;
    object.user_id = userId;
    if(userId === "") {
      object.description = "";
    } else {
      object.description = name;
    }
    let assignment = _.find(attributes.assignments, function(obj) { return obj.user_id === object.user_id})
    if(assignment && assignment.id) {
      object.schedule_assignment_id = assignment.id;
    }
    // A work order picked for someone else doesn't belong to this technician.
    if (previous && previous !== userId) {
      object.work_report_id = '';
    }
    this.revalidate();
  }

  handleTechnicianChange(event) {
    const value = event.target.value;
    this.setTechnician(value, value === '' ? '' : event.target.selectedOptions[0].text);
  }

  onStartDateChange(date) {
    let {
      jobStore: {
        timelog: {
          object
        }
      }
    } = this.props;
    object.start_time = date;
    if(object.hours && object.hours > 0 && object.start_time) {
      object.end_time = moment(object.start_time).add(parseFloat(object.hours), 'hours')._d;
    }
    this.revalidate();
  }

  // Typed hours (a string, as before) or a duration chip (a number).
  setHours(value) {
    const object = this.timelogObject();
    object.hours = value;
    const hours = parseFloat(value);
    if (hours > 0 && object.start_time) {
      object.end_time = moment(object.start_time).add(hours, 'hours')._d;
    }
    this.revalidate();
  }

  render() {
    let {
      props: {
        jobStore: {
          job: {
            object: {
              attributes
            }
          },
          jobAssociations: {
            isLoading
          },
          timelog,
          timelog: {
            object
          }
        }
      }
    } = this;
    const busy = !!timelog.isLoading;
    const close = this.handleCloseTimeLogDailog.bind(this);
    const errors = this.showErrors ? this.errors : {};
    const assignments = (attributes && attributes.assignments) || [];
    const work_orders = this.workOrders();
    const hours = parseFloat(object.hours);
    const durationValue = (DURATIONS.find((d) => d.value === hours) || {}).value;
    const ends = hours > 0 && object.start_time ? moment(object.start_time).add(hours, 'hours') : null;
    const selectedWorkOrder = _.find(work_orders, (wo) => String(wo.id) === String(object.work_report_id));

    return(
      <FormDrawer
        title="Add time log"
        icon="time"
        isOpen={this.props.isOpen}
        onClose={close}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={close} disabled={busy} className="btn-notes-close" />
            <Button
              intent={Intent.PRIMARY}
              icon="add"
              text={hours > 0 ? `Log ${hours} ${hours === 1 ? 'hour' : 'hours'}` : 'Add time log'}
              loading={busy}
              onClick={this.createTimeLog.bind(this)}
            />
          </>
        }
      >
        <FormSection
          title="Who worked"
          hint={attributes && attributes.event_number ? `Technicians on job #${attributes.event_number}.` : 'Technicians on this job.'}
        >
          <div id="timelog-field-technician" className={errors.technician ? 'has-error' : ''}>
            {assignments.length > 0 && assignments.length <= MAX_TECH_CHIPS ? (
              <Choice
                label="Technician"
                required
                value={object.user_id}
                onChange={(value) => {
                  const assignment = _.find(assignments, (a) => a.user_id === value);
                  this.setTechnician(value, assignment ? assignment.name : '');
                }}
                options={assignments.map((assignment) => ({ value: assignment.user_id, label: assignment.name }))}
                error={errors.technician}
              />
            ) : (
              <FormGroup label="Technician" labelFor="timelog-technician" labelInfo={<span className="fb-required">*</span>}>
                <div className="bp3-select bp3-fill">
                  <select
                    id="timelog-technician"
                    value={object.user_id}
                    aria-invalid={Boolean(errors.technician)}
                    onChange={this.handleTechnicianChange.bind(this)}
                  >
                    <option value="">{assignments.length ? 'Select technician' : 'No technicians on this job yet'}</option>
                    {assignments.map((assignment) => {
                      return(
                        <option value={assignment.user_id} key={assignment.user_id}>{assignment.name}</option>
                      )
                    })}
                  </select>
                </div>
                <FieldError>{errors.technician}</FieldError>
              </FormGroup>
            )}
          </div>
        </FormSection>

        <FormSection
          title="When"
          hint={ends ? `Ends ${ends.format('ddd, MMM D [at] h:mm A')}.` : 'When they started and how long they worked.'}
        >
          <div id="timelog-field-start" className={errors.start ? 'has-error' : ''}>
            <FormGroup label="Started" labelInfo={<span className="fb-required">*</span>}>
              <DateTimeInput
                placeholder={'Start'}
                defaultValue={moment(object.start_time)}
                onChange={this.onStartDateChange.bind(this)}
              />
              <FieldError>{errors.start}</FieldError>
            </FormGroup>
          </div>

          <div id="timelog-field-hours" className={`row${errors.hours ? ' has-error' : ''}`} style={{ alignItems: 'flex-start' }}>
            <div className="col-8">
              <Choice
                label="Duration"
                value={durationValue}
                onChange={(value) => this.setHours(value)}
                options={DURATIONS}
              />
            </div>
            <div className="col-4">
              <FormGroup label="Hours" labelFor="timelog-hours" labelInfo={<span className="fb-required">*</span>}>
                <input
                  id="timelog-hours"
                  type="number"
                  step="0.01"
                  min="0"
                  className="bp3-input bp3-fill"
                  placeholder="e.g. 1.5"
                  value={object.hours === undefined || object.hours === null ? '' : object.hours}
                  aria-invalid={Boolean(errors.hours)}
                  onChange={(event) => this.setHours(event.target.value)}
                />
              </FormGroup>
            </div>
          </div>
          <FieldError>{errors.hours}</FieldError>

          <Switch
            style={{ marginTop: 12 }}
            checked={Boolean(object.is_overtime)}
            label="Overtime"
            onChange={(event) => {object.is_overtime = event.target.checked}} />
        </FormSection>

        <MoreOptions
          label={selectedWorkOrder ? `Work order ${selectedWorkOrder.attributes && selectedWorkOrder.attributes.number}` : 'Work order'}
          open={Boolean(object.work_report_id)}
        >
          <FormGroup
            label="Work order"
            labelFor="timelog-work-order"
            labelInfo={isLoading ? <Spinner size={15} /> : null}
            helperText={object.user_id ? (work_orders.length ? null : "This technician has no work orders on this job.") : 'Choose a technician first.'}
          >
            <div className="bp3-select bp3-fill">
              <select
                id="timelog-work-order"
                value={object.work_report_id}
                disabled={!object.user_id}
                onChange={event => {
                  object.work_report_id = event.target.value;
                }}
              >
                <option value="">No work order</option>
                {work_orders && work_orders.length > 0 && work_orders.map((work_order) => {
                  return(
                    <option value={work_order.id} key={work_order.id}>{work_order.attributes && work_order.attributes.number}</option>
                  )
                })}
              </select>
            </div>
          </FormGroup>
        </MoreOptions>
      </FormDrawer>
    )
  }

};

export default AddTimeLogModal;
